<template>
  <div class="dashboard-page">
    <div class="dashboard-heading">
      <h2>Visão geral do local</h2>
      <div class="dashboard-tools">
        <v-select
          v-model="filters.period"
          :items="periodOptions"
          dense
          flat
          solo
          hide-details
          class="period-filter"
          @change="onPeriodChange"
        />

        <v-menu v-model="dateRangeMenu" offset-y left max-width="360" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="filter-button" :disabled="filters.period !== 'custom'" v-bind="attrs" v-on="on">
              <v-icon size="18">mdi-calendar-range-outline</v-icon>
            </v-btn>
          </template>

          <v-card class="filter-card">
            <v-card-title>Intervalo personalizado</v-card-title>
            <v-card-text>
              <v-text-field v-model="filters.start_date" label="Data inicial" type="date" outlined dense hide-details="auto" class="mb-3" />
              <v-text-field v-model="filters.end_date" label="Data final" type="date" outlined dense hide-details="auto" />
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="clearCustomRange">Limpar</v-btn>
              <v-spacer />
              <v-btn depressed color="primary" @click="applyCustomRange">Aplicar</v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </div>
    </div>

    <v-row dense class="mb-5">
      <v-col v-for="item in stats" :key="item.title" cols="12" sm="6" lg="3">
        <article :class="['metric-card', { featured: item.featured }]">
          <div class="metric-icon"><v-icon>{{ item.icon }}</v-icon></div>
          <div>
            <span>{{ item.title }}</span>
            <strong>{{ item.value }}</strong>
            <small>{{ item.caption }}</small>
          </div>
        </article>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" lg="7">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h2>Demandas do bairro</h2>
              <p>Ocorrências abertas, em andamento e concluídas</p>
            </div>
          </div>

          <div class="bars">
            <button
              v-for="month in demandChart"
              :key="month.label"
              type="button"
              class="bar-item"
              :aria-label="demandTooltipText(month)"
              @mouseenter="activeDemandMonth = month.label"
              @mouseleave="activeDemandMonth = null"
              @focus="activeDemandMonth = month.label"
              @blur="activeDemandMonth = null"
            >
              <div class="bar-track">
                <span class="bar open" :style="{ height: month.open + '%' }"></span>
                <span class="bar done" :style="{ height: month.done + '%' }"></span>
              </div>
              <div v-if="activeDemandMonth === month.label" class="bar-tooltip">
                <strong>{{ month.label }}</strong>
                <span><i class="open-dot"></i>{{ demandOpenTotal(month) }} abertas/em andamento</span>
                <span><i class="done-dot"></i>{{ demandDoneTotal(month) }} concluídas</span>
                <small>{{ demandTotal(month) }} no total</small>
              </div>
              <small>{{ month.label }}</small>
            </button>
          </div>
        </section>
      </v-col>

      <v-col cols="12" lg="5">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h2>Financeiro</h2>
              <p>Mensalidades por status</p>
            </div>
            <v-btn icon class="panel-action"><v-icon size="20">mdi-dots-horizontal</v-icon></v-btn>
          </div>

          <div class="finance-summary">
            <div class="donut" :class="{ empty: !financeTotal }" :style="financeDonutStyle">
              <span>{{ financeTotal ? `${financePaidPercent}%` : "0%" }}</span>
            </div>
            <div class="status-list">
              <div v-for="item in finance" :key="item.label">
                <span><i :style="{ background: item.color }"></i>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </div>
        </section>
      </v-col>
    </v-row>

    <v-row dense class="mt-5 equal-dashboard-row">
      <v-col cols="12" lg="7">
        <section class="panel statement-panel">
          <div class="panel-header">
            <div>
              <h2>Demonstrativo do mês</h2>
              <p>Receitas, despesas e saldo projetado pelo fluxo de caixa</p>
            </div>
          </div>

          <div class="statement-summary-grid">
            <article>
              <span>Receitas</span>
              <strong>{{ money(monthlyStatement.revenue_amount) }}</strong>
            </article>
            <article>
              <span>Despesas</span>
              <strong>{{ money(monthlyStatement.expense_amount) }}</strong>
            </article>
            <article>
              <span>Saldo final</span>
              <strong :class="{ negative: Number(monthlyStatement.final_balance || 0) < 0 }">
                {{ money(monthlyStatement.final_balance) }}
              </strong>
            </article>
            <article>
              <span>Comparação</span>
              <strong :class="statementComparisonClass">{{ statementComparisonText }}</strong>
            </article>
          </div>

          <div class="statement-bars">
            <button
              v-for="month in monthlyStatementChart"
              :key="month.label"
              type="button"
              class="statement-bar-item"
              :aria-label="statementTooltipText(month)"
              @mouseenter="activeStatementMonth = month.label"
              @mouseleave="activeStatementMonth = null"
              @focus="activeStatementMonth = month.label"
              @blur="activeStatementMonth = null"
            >
              <div class="statement-bar-track">
                <span class="statement-bar revenue" :style="{ height: statementBarHeight(month.revenueAmount) }"></span>
                <span class="statement-bar expense" :style="{ height: statementBarHeight(month.expenseAmount) }"></span>
                <span
                  class="statement-bar balance"
                  :class="{ negative: Number(month.finalBalance || 0) < 0 }"
                  :style="{ height: statementBarHeight(month.finalBalance) }"
                ></span>
              </div>
              <div v-if="activeStatementMonth === month.label" class="bar-tooltip statement-tooltip">
                <strong>{{ month.label }}</strong>
                <span><i class="revenue-dot"></i>Receitas: {{ money(month.revenueAmount) }}</span>
                <span><i class="expense-dot"></i>Despesas: {{ money(month.expenseAmount) }}</span>
                <span><i class="balance-dot"></i>Saldo: {{ money(month.finalBalance) }}</span>
              </div>
              <small>{{ month.label }}</small>
            </button>
          </div>
        </section>
      </v-col>

      <v-col cols="12" lg="5">
        <section class="panel next-assembly">
          <div class="panel-header">
            <div>
              <h2>Próxima assembleia</h2>
              <p>Agenda com assinatura de presença</p>
            </div>
          </div>
          <div class="assembly-date">
            <strong>{{ nextAssembly.day }}</strong>
            <span>{{ nextAssembly.month }}</span>
          </div>
          <h3>{{ nextAssembly.title }}</h3>
          <p>{{ nextAssembly.description }}</p>
          <v-btn small depressed color="primary" class="assembly-action-button" @click="manageAssemblySignatures">
            <v-icon left size="18">mdi-account-check-outline</v-icon>
            Gerenciar adesões
          </v-btn>
        </section>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BoaruaDashboard",
  data() {
    return {
      loading: false,
      dateRangeMenu: false,
      financePaidPercent: 0,
      activeDemandMonth: null,
      activeFinanceMonth: null,
      activeStatementMonth: null,
      filters: {
        period: "last_6_months",
        start_date: null,
        end_date: null,
      },
      periodOptions: [
        { text: "Últimos 6 meses", value: "last_6_months" },
        { text: "Últimos 3 meses", value: "last_3_months" },
        { text: "Mês atual", value: "current_month" },
        { text: "Ano atual", value: "current_year" },
        { text: "Personalizado", value: "custom" },
        { text: "Todo período", value: "all" },
      ],
      nextAssembly: {
        day: "--",
        month: "---",
        title: "Nenhuma assembleia agendada",
        description: "Quando uma nova assembleia for criada, ela aparecerá aqui.",
      },
      stats: [
        { title: "Residências", value: "0", caption: "unidades ativas", icon: "mdi-home-city-outline", featured: true },
        { title: "Demandas", value: "0", caption: "0 urgentes", icon: "mdi-clipboard-text-outline" },
        { title: "Boletos pagos", value: "0%", caption: "0 de 0", icon: "mdi-barcode" },
        { title: "Assembleias", value: "0", caption: "próximas agendas", icon: "mdi-account-voice" },
      ],
      demandChart: [
        { label: "Jan", open: 46, done: 74 },
        { label: "Fev", open: 64, done: 58 },
        { label: "Mar", open: 82, done: 86 },
        { label: "Abr", open: 44, done: 62 },
        { label: "Mai", open: 68, done: 80 },
        { label: "Jun", open: 38, done: 55 },
      ],
      finance: [
        { label: "Pagos", value: 0, color: "#057A3D" },
        { label: "Pendentes", value: 0, color: "#99D7B5" },
        { label: "Vencidos", value: 0, color: "#E53935" },
      ],
      financeChart: [],
      monthlyStatement: {
        reference_month: "",
        revenue_amount: 0,
        expense_amount: 0,
        final_balance: 0,
        comparison: {
          balance_delta: 0,
        },
      },
      monthlyStatementChart: [],
    };
  },
  computed: {
    financeTotal() {
      return this.finance.reduce((total, item) => total + Number(item.value || 0), 0);
    },
    financeDonutStyle() {
      if (!this.financeTotal) {
        return {
          background: "conic-gradient(#e5ece7 0 100%)",
        };
      }

      let cursor = 0;
      const segments = this.finance.map((item) => {
        const value = Number(item.value || 0);
        const start = cursor;
        const end = cursor + (value / this.financeTotal) * 100;
        cursor = end;
        return `${item.color} ${start}% ${end}%`;
      });

      return {
        background: `conic-gradient(${segments.join(", ")})`,
      };
    },
    financeChartMaxAmount() {
      return Math.max(
        1,
        ...this.financeChart.map((month) => Math.max(
          Number(month.paidAmount || 0),
          Number(month.pendingAmount || 0),
          Number(month.overdueAmount || 0)
        ))
      );
    },
    statementChartMaxAmount() {
      return Math.max(
        1,
        ...this.monthlyStatementChart.map((month) => Math.max(
          Math.abs(Number(month.revenueAmount || 0)),
          Math.abs(Number(month.expenseAmount || 0)),
          Math.abs(Number(month.finalBalance || 0))
        ))
      );
    },
    statementComparisonText() {
      const delta = Number(this.monthlyStatement.comparison && this.monthlyStatement.comparison.balance_delta || 0);
      if (!delta) return "Sem variação";
      return `${delta > 0 ? "+" : ""}${this.money(delta)}`;
    },
    statementComparisonClass() {
      const delta = Number(this.monthlyStatement.comparison && this.monthlyStatement.comparison.balance_delta || 0);
      if (delta > 0) return "positive";
      if (delta < 0) return "negative";
      return "";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const response = await axios.get("/dashboards/boarua-admin-overview", {
          params: this.activeFilters(),
        });
        const dashboard = response.data.content || {};
        this.stats = dashboard.stats || this.stats;
        this.demandChart = dashboard.demandChart || this.demandChart;
        this.finance = dashboard.finance || this.finance;
        this.financeChart = dashboard.financeChart || this.buildFinanceChartFallback();
        this.monthlyStatement = dashboard.monthlyStatement || this.buildMonthlyStatementFallback();
        this.monthlyStatementChart = dashboard.monthlyStatementChart || this.buildMonthlyStatementChartFallback();
        this.financePaidPercent = dashboard.financePaidPercent || 0;
        this.nextAssembly = dashboard.nextAssembly || this.nextAssembly;
      } finally {
        this.loading = false;
      }
    },
    onPeriodChange() {
      if (this.filters.period !== "custom") {
        this.filters.start_date = null;
        this.filters.end_date = null;
        this.load();
        return;
      }

      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      this.filters.start_date = this.toDateInput(firstDay);
      this.filters.end_date = this.toDateInput(now);
      this.dateRangeMenu = true;
    },
    applyCustomRange() {
      this.dateRangeMenu = false;
      this.load();
    },
    clearCustomRange() {
      this.filters.start_date = null;
      this.filters.end_date = null;
      this.dateRangeMenu = false;
      this.load();
    },
    toDateInput(value) {
      const date = new Date(value);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    activeFilters() {
      return Object.keys(this.filters).reduce((params, key) => {
        if (this.filters[key] !== null && this.filters[key] !== "") {
          params[key] = this.filters[key];
        }
        return params;
      }, {});
    },
    demandOpenTotal(month = {}) {
      return Number(month.openTotal ?? month.open_total ?? month.openCount ?? month.open_count ?? 0);
    },
    demandDoneTotal(month = {}) {
      return Number(month.doneTotal ?? month.done_total ?? month.doneCount ?? month.done_count ?? 0);
    },
    demandTotal(month = {}) {
      return this.demandOpenTotal(month) + this.demandDoneTotal(month);
    },
    demandTooltipText(month = {}) {
      return `${month.label}: ${this.demandOpenTotal(month)} abertas ou em andamento, ${this.demandDoneTotal(month)} concluídas, ${this.demandTotal(month)} no total`;
    },
    buildFinanceChartFallback() {
      const current = this.finance.reduce((result, item) => {
        const label = String(item.label || "").toLowerCase();
        if (label.includes("pago")) result.paidAmount = Number(item.value || 0);
        if (label.includes("pend")) result.pendingAmount = Number(item.value || 0);
        if (label.includes("venc")) result.overdueAmount = Number(item.value || 0);
        return result;
      }, { paidAmount: 0, pendingAmount: 0, overdueAmount: 0 });

      return this.monthLabels(6).map((label, index, months) => ({
        label,
        paidAmount: index === months.length - 1 ? current.paidAmount : 0,
        pendingAmount: index === months.length - 1 ? current.pendingAmount : 0,
        overdueAmount: index === months.length - 1 ? current.overdueAmount : 0,
      }));
    },
    emptyMonthlyStatement() {
      return {
        reference_month: "",
        revenue_amount: 0,
        expense_amount: 0,
        final_balance: 0,
        comparison: {
          balance_delta: 0,
        },
      };
    },
    buildMonthlyStatementFallback() {
      const current = this.financeChart[this.financeChart.length - 1] || {};
      const revenueAmount = Number(current.paidAmount || 0);
      return {
        reference_month: "",
        revenue_amount: revenueAmount,
        expense_amount: 0,
        final_balance: revenueAmount,
        comparison: {
          balance_delta: 0,
        },
      };
    },
    buildMonthlyStatementChartFallback() {
      return this.financeChart.map((month) => ({
        label: month.label,
        revenueAmount: Number(month.paidAmount || 0),
        expenseAmount: 0,
        finalBalance: Number(month.paidAmount || 0),
      }));
    },
    monthLabels(total) {
      const labels = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
      const now = new Date();
      return Array.from({ length: total }).map((_, index) => {
        const date = new Date(now.getFullYear(), now.getMonth() - (total - index - 1), 1);
        return labels[date.getMonth()];
      });
    },
    financeBarHeight(value) {
      const amount = Number(value || 0);
      if (!amount) return "8%";
      return `${Math.max(12, Math.round((amount / this.financeChartMaxAmount) * 100))}%`;
    },
    financeMonthTotal(month = {}) {
      return Number(month.paidAmount || 0) + Number(month.pendingAmount || 0) + Number(month.overdueAmount || 0);
    },
    statementBarHeight(value) {
      const amount = Math.abs(Number(value || 0));
      if (!amount) return "8%";
      return `${Math.max(12, Math.round((amount / this.statementChartMaxAmount) * 100))}%`;
    },
    money(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    financeTooltipText(month = {}) {
      return `${month.label}: pagos ${this.money(month.paidAmount)}, pendentes ${this.money(month.pendingAmount)}, vencidos ${this.money(month.overdueAmount)}`;
    },
    statementTooltipText(month = {}) {
      return `${month.label}: receitas ${this.money(month.revenueAmount)}, despesas ${this.money(month.expenseAmount)}, saldo ${this.money(month.finalBalance)}`;
    },
    manageAssemblySignatures() {
      const query = this.nextAssembly && this.nextAssembly.id
        ? { assembly_id: this.nextAssembly.id }
        : {};
      this.$router.push({ path: "/assembleias", query });
    },
  },
};
</script>

<style scoped lang="scss">
.dashboard-page {
  position: relative;
  color: #111827;
}

.dashboard-heading {
  margin-bottom: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  h2 {
    margin: 0;
    color: #111111;
    font-size: 28px;
    font-weight: 600;
  }
}

.dashboard-tools {
  display: flex;
  align-items: center;
  gap: 10px;
}

.period-filter::v-deep {
  width: 210px;

  .v-input__control {
    min-height: 42px;
  }

  .v-input__slot {
    min-height: 42px !important;
    background: #ededeb !important;
    border-radius: 18px;
    box-shadow: none !important;
  }

  .v-select__selection {
    font-size: 12px;
  }
}

.filter-button {
  width: 40px;
  height: 40px;
  color: #111111;
  background: #ededeb;
  border: 1px solid transparent;
}

.filter-card {
  border-radius: 16px;

  .v-card__title {
    color: #111111;
    font-size: 16px;
    font-weight: 700;
  }
}

.metric-card,
.panel {
  background: #ffffff;
  border: 0;
  border-radius: 16px;
  box-shadow: none;
}

.metric-card {
  min-height: 146px;
  padding: 18px;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  overflow: hidden;

  &.featured {
    color: #ffffff;
    background: #004f45;

    .metric-icon {
      background: rgba(255, 255, 255, 0.16);

      .v-icon {
        color: #ffffff;
      }
    }

    small,
    span {
      color: rgba(255, 255, 255, 0.78);
    }
  }

  span,
  small {
    display: block;
    color: #66736b;
  }

  span {
    font-size: 13px;
  }

  strong {
    display: block;
    margin: 7px 0 4px;
    font-size: 28px;
    line-height: 1;
  }

  small {
    font-size: 12px;
  }
}

.metric-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef7f1;
  border-radius: 12px;

  .v-icon {
    color: #057a3d;
  }
}

.panel {
  padding: 22px;
}

.equal-dashboard-row {
  align-items: stretch;

  > .col,
  > [class*="col-"] {
    display: flex;
  }

  .panel {
    width: 100%;
  }
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    font-size: 17px;
  }

  p {
    margin: 3px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.panel-action {
  border: 1px solid #e1e7e0;
}

.bars {
  position: relative;
  height: 238px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  align-items: end;
  gap: 18px;
}

.bar-item {
  position: relative;
  padding: 0;
  border: 0;
  background: transparent;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  cursor: default;

  small {
    color: #7a827b;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible .bar-track,
  &:hover .bar-track {
    background: #f5faf7;
    border-color: #d9eee1;
  }

  &:focus-visible .bar,
  &:hover .bar {
    filter: saturate(1.15);
    transform: translateY(-2px);
  }
}

.bar-track {
  position: relative;
  width: 100%;
  height: 190px;
  padding: 0 8px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: 12px;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.bar {
  width: 18px;
  display: block;
  border-radius: 8px 8px 0 0;
  transition: transform 0.18s ease, filter 0.18s ease;

  &.open {
    background: linear-gradient(180deg, #a8ddbf, #e5f3ea);
  }

  &.done {
    background: linear-gradient(180deg, #057a3d, #022d1a);
  }
}

.finance-bars {
  position: relative;
  height: 238px;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  align-items: end;
  gap: 18px;
}

.finance-bar-item {
  position: relative;
  height: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  background: transparent;
  border: 0;
  cursor: default;

  > small {
    color: #7a827b;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible .finance-bar-track,
  &:hover .finance-bar-track {
    background: #f5faf7;
    border-color: #d9eee1;
  }

  &:focus-visible .finance-bar,
  &:hover .finance-bar {
    filter: saturate(1.15);
    transform: translateY(-2px);
  }
}

.finance-bar-track {
  width: 100%;
  height: 190px;
  padding: 0 8px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 7px;
  border: 1px solid transparent;
  border-radius: 12px;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.finance-bar {
  width: 16px;
  display: block;
  border-radius: 8px 8px 0 0;
  transition: transform 0.18s ease, filter 0.18s ease;

  &.paid {
    background: linear-gradient(180deg, #057a3d, #02351f);
  }

  &.pending {
    background: linear-gradient(180deg, #9bd9b6, #dff2e6);
  }

  &.overdue {
    background: linear-gradient(180deg, #e53935, #ffb4b4);
  }
}

.bar-tooltip {
  position: absolute;
  left: 50%;
  bottom: 34px;
  z-index: 3;
  width: max-content;
  max-width: 240px;
  padding: 10px 12px;
  color: #10223f;
  background: #ffffff;
  border: 1px solid #dce8e1;
  border-radius: 10px;
  box-shadow: 0 16px 36px rgba(16, 34, 63, 0.14);
  transform: translateX(-50%);
  pointer-events: none;

  strong,
  span,
  small {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #10223f;
    text-align: left;
    white-space: nowrap;
  }

  strong {
    margin-bottom: 6px;
    font-size: 13px;
  }

  span,
  small {
    font-size: 12px;
  }

  small {
    margin-top: 6px;
    color: #66736b;
  }

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -6px;
    width: 10px;
    height: 10px;
    background: #ffffff;
    border-right: 1px solid #dce8e1;
    border-bottom: 1px solid #dce8e1;
    transform: translateX(-50%) rotate(45deg);
  }
}

.finance-tooltip {
  bottom: 34px;
}

.open-dot,
.done-dot,
.paid-dot,
.pending-dot,
.overdue-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 8px;
}

.open-dot {
  background: #a8ddbf;
}

.done-dot {
  background: #057a3d;
}

.paid-dot {
  background: #057a3d;
}

.pending-dot {
  background: #99d7b5;
}

.overdue-dot {
  background: #e53935;
}

.statement-summary-grid {
  margin-bottom: 18px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  article {
    min-height: 72px;
    padding: 12px;
    background: #f8fbf9;
    border: 1px solid #e1e8e2;
    border-radius: 12px;
  }

  span {
    display: block;
    margin-bottom: 8px;
    color: #66736b;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    display: block;
    color: #10223f;
    font-size: 17px;
    line-height: 1.2;

    &.positive {
      color: #057a3d;
    }

    &.negative {
      color: #b91c1c;
    }
  }
}

.statement-bars {
  position: relative;
  height: 210px;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  align-items: end;
  gap: 16px;
}

.statement-bar-item {
  position: relative;
  height: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  background: transparent;
  border: 0;
  cursor: default;

  > small {
    color: #7a827b;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible .statement-bar-track,
  &:hover .statement-bar-track {
    background: #f5faf7;
    border-color: #d9eee1;
  }

  &:focus-visible .statement-bar,
  &:hover .statement-bar {
    filter: saturate(1.15);
    transform: translateY(-2px);
  }
}

.statement-bar-track {
  width: 100%;
  height: 165px;
  padding: 0 8px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: 12px;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.statement-bar {
  width: 15px;
  display: block;
  border-radius: 8px 8px 0 0;
  transition: transform 0.18s ease, filter 0.18s ease;

  &.revenue {
    background: linear-gradient(180deg, #057a3d, #02351f);
  }

  &.expense {
    background: linear-gradient(180deg, #e53935, #ffb4b4);
  }

  &.balance {
    background: linear-gradient(180deg, #10223f, #6b7280);

    &.negative {
      background: linear-gradient(180deg, #b91c1c, #fca5a5);
    }
  }
}

.statement-tooltip {
  bottom: 30px;
}

.revenue-dot,
.expense-dot,
.balance-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 8px;
}

.revenue-dot {
  background: #057a3d;
}

.expense-dot {
  background: #e53935;
}

.balance-dot {
  background: #10223f;
}

.finance-summary {
  min-height: 238px;
  display: grid;
  grid-template-columns: 170px 1fr;
  align-items: center;
  gap: 22px;
}

.donut {
  width: 156px;
  height: 156px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  border-radius: 50%;

  span {
    width: 78px;
    height: 78px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #057a3d;
    border-radius: 50%;
    font-size: 22px;
    font-weight: 800;
  }

  &.empty {
    color: #66736b;

    span {
      background: #ffffff;
      color: #66736b;
      border: 1px solid #dfe7df;
    }
  }
}

.status-list div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.status-list div {
  padding: 10px 0;
  border-bottom: 1px solid #edf1ec;

  span {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #66736b;
  }

  i {
    width: 9px;
    height: 9px;
    border-radius: 50%;
  }
}

.next-assembly {
  display: flex;
  flex-direction: column;

  .assembly-date {
    width: 74px;
    height: 74px;
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    background: #057a3d;
    border-radius: 8px;

    strong {
      font-size: 28px;
      line-height: 1;
    }

    span {
      font-size: 12px;
      font-weight: 700;
    }
  }

  h3 {
    margin: 0 0 8px;
    font-size: 20px;
  }

  p {
    margin-bottom: 18px;
    color: #66736b;
  }

  .v-btn {
    margin-top: auto;
  }

  .assembly-action-button {
    align-self: stretch;
    min-height: 32px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
  }
}

@media (max-width: 700px) {
  .dashboard-heading {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }

  .dashboard-heading h2 {
    font-size: 28px;
    line-height: 1.15;
  }

  .dashboard-tools {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 42px;
    align-items: center;
    gap: 8px;
  }

  .period-filter::v-deep {
    width: 100%;
  }

  .finance-summary {
    grid-template-columns: 1fr;
  }

  .bars {
    height: 208px;
    gap: 8px;
    overflow: hidden;
  }

  .bar-track {
    height: 160px;
    padding: 0 4px;
    gap: 5px;
  }

  .bar {
    width: 14px;
  }

  .finance-bars {
    grid-template-columns: 1fr;
  }

  .finance-bars {
    height: auto;
    gap: 12px;
  }

  .statement-bars {
    height: 188px;
    grid-template-columns: repeat(6, minmax(38px, 1fr));
    gap: 6px;
    overflow: hidden;
  }

  .statement-bar-track {
    height: 142px;
    padding: 0 3px;
    gap: 4px;
  }

  .statement-bar {
    width: 11px;
  }

  .statement-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .panel {
    padding: 22px;
    overflow: hidden;
  }
}
</style>
